/**
 * Map view persistence — local cache (fast, synchronous) + server sync (debounced).
 *
 * Cold-load resolver priority:
 *   1. `sessionStorage` in-session view (existing behaviour, handled by consumer contexts)
 *   2. Local persistent cache (this module) — populated from the server on previous mounts
 *   3. User's timezone coords (getTimezoneCoords) — handled by consumer hook
 *   4. Sydney fallback — handled by consumer hook
 *
 * Save path (called from consumer on debounced map pan/zoom):
 *   a) Write to localStorage immediately (so next cold-load is instant).
 *   b) PATCH server (fire-and-forget; server rate-limits to 2 writes/sec/user).
 */
import { getDeviceFingerprint } from './deviceFingerprint';

const KEY_PREFIX = 'ensue_map_view';
const STALE_AFTER_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

const localKey = (page, fp) => `${KEY_PREFIX}::${page}::${fp}`;

/** Read the persistent local view for this page/device; returns null if missing or stale. */
export const readLocalMapView = (page) => {
  try {
    const fp = getDeviceFingerprint();
    const raw = localStorage.getItem(localKey(page, fp));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.lat !== 'number' || typeof parsed.lng !== 'number') return null;
    const updatedAt = parsed.updated_at ? Date.parse(parsed.updated_at) : 0;
    if (updatedAt && Date.now() - updatedAt > STALE_AFTER_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

/** Write the persistent local view. */
export const writeLocalMapView = (page, view) => {
  try {
    const fp = getDeviceFingerprint();
    localStorage.setItem(
      localKey(page, fp),
      JSON.stringify({ ...view, updated_at: view.updated_at || new Date().toISOString() }),
    );
  } catch { /* localStorage full/blocked — degrade gracefully */ }
};

/** Async: fetch the saved view from the server; returns null on missing / error. */
export const fetchServerMapView = async (page, apiBase, authToken) => {
  try {
    const fp = getDeviceFingerprint();
    const url = `${apiBase}/api/users/me/map-view?page=${encodeURIComponent(page)}&device_fp=${encodeURIComponent(fp)}`;
    const resp = await fetch(url, { headers: { Authorization: `Bearer ${authToken}` } });
    if (!resp.ok) return null;
    const data = await resp.json();
    return data || null;
  } catch {
    return null;
  }
};

/** Async: save the view to the server (fire-and-forget). */
export const saveServerMapView = async (page, view, apiBase, authToken) => {
  try {
    const fp = getDeviceFingerprint();
    await fetch(`${apiBase}/api/users/me/map-view`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify({ page, device_fp: fp, lat: view.lat, lng: view.lng, zoom: view.zoom }),
    });
  } catch { /* server unreachable / rate-limited — local cache already persisted */ }
};

/**
 * Create a debounced saver bound to a specific page. Returns a function that
 * accepts a new view. Consecutive rapid calls are collapsed to one save 1500 ms
 * after the last call. Skips saves that are too small to matter.
 */
export const createDebouncedMapViewSaver = (page, apiBase, getAuthToken, debounceMs = 1500) => {
  let timer = null;
  let lastSaved = null;
  const MIN_DISTANCE_METERS = 500;
  const MIN_ZOOM_DELTA = 1;

  const metersBetween = (a, b) => {
    // Haversine distance
    const toRad = (d) => d * Math.PI / 180;
    const R = 6371000;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  };

  return (view) => {
    if (!view || typeof view.lat !== 'number' || typeof view.lng !== 'number') return;
    if (lastSaved) {
      const zoomDelta = Math.abs((view.zoom || 0) - (lastSaved.zoom || 0));
      const dist = metersBetween(lastSaved, view);
      if (zoomDelta < MIN_ZOOM_DELTA && dist < MIN_DISTANCE_METERS) return; // change too small
    }
    if (timer) clearTimeout(timer);
    timer = setTimeout(async () => {
      timer = null;
      const stamped = { ...view, updated_at: new Date().toISOString() };
      writeLocalMapView(page, stamped);
      lastSaved = stamped;
      const token = typeof getAuthToken === 'function' ? getAuthToken() : getAuthToken;
      if (token) await saveServerMapView(page, stamped, apiBase, token);
    }, debounceMs);
  };
};
